import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { 
  Plus, 
  Trash2, 
  GripVertical,
  Loader2,
  Star
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OptionSetEditor({ optionSetId, onClose, onSave }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState({
    name: '',
    code: '',
    description: '',
    type: 'select',
    required: true,
    display_order: 0,
    applicable_types: [],
    choices: []
  });

  // Fetch existing option set if editing
  useEffect(() => {
    if (optionSetId) {
      fetchOptionSet();
    }
  }, [optionSetId]);

  const fetchOptionSet = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/option-sets/${optionSetId}`);
      const data = await res.json();
      setForm({
        name: data.name || '',
        code: data.code || '',
        description: data.description || '',
        type: data.type || 'select',
        required: data.required ?? true,
        display_order: data.display_order || 0,
        applicable_types: data.applicable_types || [],
        choices: data.choices || []
      });
    } catch (error) {
      console.error('Error fetching option set:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const method = optionSetId ? 'PUT' : 'POST';
      const url = optionSetId 
        ? `${API_URL}/api/option-sets/${optionSetId}`
        : `${API_URL}/api/option-sets`;

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        onSave();
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to save option set');
      }
    } catch (error) {
      console.error('Error saving option set:', error);
      alert('Failed to save option set');
    } finally {
      setSaving(false);
    }
  };

  const addChoice = () => {
    const newChoice = {
      id: `ch_${Date.now().toString(36)}`,
      name: '',
      description: '',
      is_default: form.choices.length === 0,
      is_active: true,
      sort_order: form.choices.length,
      price_modifier: { type: 'percent', value: 0 }
    };
    setForm(prev => ({
      ...prev,
      choices: [...prev.choices, newChoice]
    }));
  };

  const updateChoice = (index, field, value) => {
    setForm(prev => {
      const choices = [...prev.choices];
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        choices[index] = {
          ...choices[index],
          [parent]: { ...choices[index][parent], [child]: value }
        };
      } else {
        choices[index] = { ...choices[index], [field]: value };
      }
      return { ...prev, choices };
    });
  };

  const removeChoice = (index) => {
    setForm(prev => ({
      ...prev,
      choices: prev.choices.filter((_, i) => i !== index)
    }));
  };

  const setDefaultChoice = (index) => {
    setForm(prev => ({
      ...prev,
      choices: prev.choices.map((choice, i) => ({
        ...choice,
        is_default: i === index
      }))
    }));
  };

  const toggleProductType = (type) => {
    setForm(prev => ({
      ...prev,
      applicable_types: prev.applicable_types.includes(type)
        ? prev.applicable_types.filter(t => t !== type)
        : [...prev.applicable_types, type]
    }));
  };

  if (loading) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-3xl">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {optionSetId ? 'Edit Option Set' : 'Create New Option Set'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Option Set Name *</Label>
                  <Input 
                    value={form.name}
                    onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Paper Stock"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Code *</Label>
                  <Input 
                    value={form.code}
                    onChange={(e) => setForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                    placeholder="e.g., PAPER"
                    maxLength={20}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea 
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of this option"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Display Type</Label>
                  <Select 
                    value={form.type} 
                    onValueChange={(v) => setForm(prev => ({ ...prev, type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="select">Dropdown</SelectItem>
                      <SelectItem value="radio">Radio Buttons</SelectItem>
                      <SelectItem value="checkbox">Checkboxes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Display Order</Label>
                  <Input 
                    type="number"
                    value={form.display_order}
                    onChange={(e) => setForm(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Required</Label>
                  <div className="flex items-center h-10">
                    <Switch 
                      checked={form.required}
                      onCheckedChange={(checked) => setForm(prev => ({ ...prev, required: checked }))}
                    />
                    <span className="ml-2 text-sm text-gray-500">{form.required ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              </div>

              {/* Applicable Product Types */}
              <div className="space-y-2">
                <Label>Applicable Product Types</Label>
                <div className="flex gap-2">
                  {['LGF', 'SMF', 'AWD', 'PROMO'].map(type => (
                    <Badge 
                      key={type}
                      variant={form.applicable_types.includes(type) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleProductType(type)}
                    >
                      {type}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Choices */}
          <Card>
            <CardHeader className="py-3 flex flex-row items-center justify-between">
              <CardTitle className="text-sm">Choices</CardTitle>
              <Button variant="outline" size="sm" onClick={addChoice}>
                <Plus className="h-4 w-4 mr-1" /> Add Choice
              </Button>
            </CardHeader>
            <CardContent>
              {form.choices.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No choices yet. Click "Add Choice" to create options.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {form.choices.map((choice, idx) => (
                    <div key={choice.id || idx} className="border rounded-lg p-4 relative">
                      <div className="absolute top-2 right-2 flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setDefaultChoice(idx)}
                          title="Set as default"
                        >
                          <Star className={`h-4 w-4 ${choice.is_default ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeChoice(idx)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 gap-4 pr-20">
                        <div className="space-y-2">
                          <Label className="text-xs">Choice Name *</Label>
                          <Input 
                            value={choice.name}
                            onChange={(e) => updateChoice(idx, 'name', e.target.value)}
                            placeholder="e.g., 14pt Cardstock"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Description</Label>
                          <Input 
                            value={choice.description || ''}
                            onChange={(e) => updateChoice(idx, 'description', e.target.value)}
                            placeholder="Brief description"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4 mt-3">
                        <div className="space-y-2">
                          <Label className="text-xs">Price Modifier Type</Label>
                          <Select 
                            value={choice.price_modifier?.type || 'percent'} 
                            onValueChange={(v) => updateChoice(idx, 'price_modifier.type', v)}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="percent">Percent (%)</SelectItem>
                              <SelectItem value="fixed">Fixed ($)</SelectItem>
                              <SelectItem value="per_sqft">Per Sq Ft</SelectItem>
                              <SelectItem value="per_unit">Per Unit</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Modifier Value</Label>
                          <div className="flex items-center">
                            {choice.price_modifier?.type !== 'percent' && (
                              <span className="text-gray-500 mr-1">$</span>
                            )}
                            <Input 
                              type="number"
                              step={choice.price_modifier?.type === 'percent' ? '1' : '0.01'}
                              value={choice.price_modifier?.value || 0}
                              onChange={(e) => updateChoice(idx, 'price_modifier.value', parseFloat(e.target.value) || 0)}
                              className="h-9"
                            />
                            {choice.price_modifier?.type === 'percent' && (
                              <span className="text-gray-500 ml-1">%</span>
                            )}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">Status</Label>
                          <div className="flex items-center h-9">
                            <Switch 
                              checked={choice.is_active !== false}
                              onCheckedChange={(checked) => updateChoice(idx, 'is_active', checked)}
                            />
                            <span className="ml-2 text-xs text-gray-500">
                              {choice.is_active !== false ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {choice.is_default && (
                        <Badge className="mt-2 bg-yellow-100 text-yellow-800">Default Choice</Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !form.name || !form.code}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {optionSetId ? 'Save Changes' : 'Create Option Set'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
